import { TooltipProps } from '@mui/material/Tooltip';

/**
 * Global Tooltip Configuration
 * Following Material Design 3 and WCAG 2.2 Level AA Standards
 * 
 * Design Specifications:
 * - Background: #212121 (matches sidebar dark gray)
 * - Text: #FFFFFF (high contrast for accessibility)
 * - Font: Roboto 12px weight 400
 * - Timing: 400ms enter delay, 200ms leave delay
 * - Arrow indicators: Always visible for better visual connection
 */

export const GLOBAL_TOOLTIP_PROPS: Partial<TooltipProps> = {
  arrow: true,
  enterDelay: 400,
  enterNextDelay: 200, // Faster when hovering over multiple tooltips in sequence
  leaveDelay: 200,
  placement: 'bottom',
  slotProps: {
    popper: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -8], // 8px spacing between target and tooltip
          },
        },
      ],
    },
    tooltip: {
      sx: {
        bgcolor: '#212121',
        color: '#FFFFFF',
        fontSize: '12px',
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 400,
        padding: '6px 12px',
        borderRadius: '4px',
        maxWidth: '300px',
        lineHeight: 1.5,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
        '& .MuiTooltip-arrow': {
          color: '#212121',
        },
      },
    },
  },
};

/**
 * Common Tooltip Text Constants
 * Centralized for consistency across the application
 */
export const COMMON_TOOLTIPS = {
  // Navigation
  search: 'Search',
  filter: 'Filter results',
  sort: 'Sort',
  refresh: 'Refresh data',
  close: 'Close',
  back: 'Go back',
  forward: 'Go forward',
  
  // CRUD Operations
  add: 'Add new',
  edit: 'Edit',
  delete: 'Delete',
  save: 'Save changes',
  cancel: 'Cancel',
  
  // View Controls
  listView: 'List view',
  gridView: 'Grid view',
  expand: 'Expand',
  collapse: 'Collapse',
  fullscreen: 'Toggle fullscreen mode',
  exitFullscreen: 'Exit fullscreen mode',
  
  // Media Controls
  play: 'Play',
  pause: 'Pause',
  stop: 'Stop',
  rotateLeft: 'Rotate left',
  rotateRight: 'Rotate right',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  resetZoom: 'Reset zoom',
  
  // File Operations
  upload: 'Upload file',
  download: 'Download',
  share: 'Share',
  copy: 'Copy',
  move: 'Move',
  rename: 'Rename',
  
  // Common Actions
  more: 'More options',
  info: 'Information',
  help: 'Help',
  settings: 'Settings',
  notifications: 'Notifications',
  messages: 'Messages',
  profile: 'User profile',
};

/**
 * Tooltip text for disabled states
 * Explains WHY the action is unavailable
 */
export const DISABLED_TOOLTIPS = {
  prevDateDisabled: 'No earlier data available',
  nextDateDisabled: 'Already at today',
  deleteDisabled: 'Select items first',
  saveDisabled: 'No changes to save',
  uploadDisabled: 'Maximum upload limit reached',
  submitDisabled: 'Complete required fields',
};

/**
 * Helper function to get tooltip props with custom placement
 */
export const getTooltipProps = (
  placement?: TooltipProps['placement'],
  customProps?: Partial<TooltipProps>
): Partial<TooltipProps> => {
  return {
    ...GLOBAL_TOOLTIP_PROPS,
    placement: placement || GLOBAL_TOOLTIP_PROPS.placement,
    ...customProps,
  };
};

/**
 * Placement rules by component location
 */
export const TOOLTIP_PLACEMENTS = {
  header: 'bottom' as const,
  sidebar: 'right' as const,
  footer: 'top' as const,
  tableRow: 'left' as const,
  floatingButton: 'left' as const,
};